import React, { useEffect, useRef } from "react";

import {
    FaCode,
    FaGlobe,
    FaDatabase,
    FaSchool,
    FaServer,
    FaMobileAlt,
    FaRobot,
    FaCloud,
    FaArrowRight,
    FaLayerGroup,
    FaCogs,
    FaShieldAlt,
    FaRocket,
} from "react-icons/fa";

import technologyImage from "./faith.jpg";

const services = [
    {
        id: 1,
        icon: <FaCode />,
        number: "01",
        title: "Software Development",
        description:
            "We design and develop powerful custom software systems built around the unique needs of businesses, organizations, and institutions.",
    },
    {
        id: 2,
        icon: <FaGlobe />,
        number: "02",
        title: "Web Design & Development",
        description:
            "Modern, responsive, high-performance websites and web applications designed to create strong digital experiences.",
    },
    {
        id: 3,
        icon: <FaDatabase />,
        number: "03",
        title: "Database Systems",
        description:
            "Secure and scalable database solutions for organizing, managing, protecting, and accessing critical business information.",
    },
    {
        id: 4,
        icon: <FaSchool />,
        number: "04",
        title: "School Management Systems",
        description:
            "Complete digital platforms for managing students, teachers, academics, attendance, fees, communication, and school operations.",
    },
    {
        id: 5,
        icon: <FaServer />,
        number: "05",
        title: "API & Backend Development",
        description:
            "Reliable backend architecture, REST APIs, authentication systems, integrations, and scalable server-side applications.",
    },
    {
        id: 6,
        icon: <FaMobileAlt />,
        number: "06",
        title: "Application Development",
        description:
            "Functional digital applications that connect users, organizations, data, and services across modern devices and platforms.",
    },
    {
        id: 7,
        icon: <FaRobot />,
        number: "07",
        title: "AI & Automation",
        description:
            "Intelligent technology and automation solutions that reduce repetitive work and help organizations operate more efficiently.",
    },
    {
        id: 8,
        icon: <FaCloud />,
        number: "08",
        title: "Cloud & Digital Solutions",
        description:
            "Deployment, hosting, cloud-ready systems, digital infrastructure, and technology solutions built for reliability and growth.",
    },
];

const Card = () => {
    const sectionRef = useRef(null);

    useEffect(() => {
        const section = sectionRef.current;

        if (!section) return;

        const handleMouseMove = (event) => {
            const rect = section.getBoundingClientRect();

            const x =
                ((event.clientX - rect.left) / rect.width) * 100;

            const y =
                ((event.clientY - rect.top) / rect.height) * 100;

            section.style.setProperty(
                "--mouse-x",
                `${x}%`
            );

            section.style.setProperty(
                "--mouse-y",
                `${y}%`
            );
        };

        section.addEventListener("mousemove", handleMouseMove);

        return () => {
            section.removeEventListener(
                "mousemove",
                handleMouseMove
            );
        };
    }, []);

    return (
        <section
            ref={sectionRef}
            className="polu-tech-section"
        >
            <div className="polu-tech-background">
                <div className="polu-grid"></div>

                <div className="polu-energy polu-energy-one"></div>

                <div className="polu-energy polu-energy-two"></div>

                <div className="polu-energy polu-energy-three"></div>

                <div className="polu-orb polu-orb-one"></div>

                <div className="polu-orb polu-orb-two"></div>

                <div className="polu-orb polu-orb-three"></div>

                <div className="polu-particles">
                    {Array.from({ length: 24 }).map(
                        (_, index) => (
                            <span
                                key={index}
                                style={{
                                    "--particle-delay": `${(
                                        index * 0.27
                                    ).toFixed(2)}s`,
                                    "--particle-x": `${
                                        (index * 37) % 100
                                    }%`,
                                    "--particle-y": `${
                                        (index * 61) % 100
                                    }%`,
                                }}
                            />
                        )
                    )}
                </div>
            </div>

            <div className="polu-tech-container">
                <div className="polu-tech-intro">
                    <div className="polu-intro-left">
                        <div className="polu-section-label">
                            <span className="polu-label-line"></span>

                            <span>
                                POLU-TECH
                            </span>

                            <span className="polu-label-line"></span>
                        </div>

                        <h2 className="polu-main-title">
                            Technology
                            <span>
                                Without Limits.
                            </span>
                        </h2>

                        <p className="polu-main-description">
                            We build modern digital solutions
                            that transform ideas into powerful,
                            reliable, and scalable technology.
                        </p>

                        <div className="polu-intro-actions">
                            <a
                                href="#services"
                                className="polu-primary-button"
                            >
                                Explore Our Solutions

                                <FaArrowRight />
                            </a>

                            <div className="polu-live-status">
                                <span className="polu-status-dot"></span>

                                <span>
                                    INNOVATION IN MOTION
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="polu-intro-right">
                        <div className="polu-tech-visual">
                            <div className="polu-visual-ring ring-one"></div>

                            <div className="polu-visual-ring ring-two"></div>

                            <div className="polu-visual-ring ring-three"></div>

                            <div className="polu-image-frame">
                                <img
                                    src={technologyImage}
                                    alt="POLU-TECH technology development"
                                />

                                <div className="polu-image-overlay"></div>

                                <div className="polu-image-content">
                                    <span>
                                        DIGITAL
                                    </span>

                                    <strong>
                                        INNOVATION
                                    </strong>
                                </div>
                            </div>

                            <div className="polu-floating-icon icon-code">
                                <FaCode />
                            </div>

                            <div className="polu-floating-icon icon-database">
                                <FaDatabase />
                            </div>

                            <div className="polu-floating-icon icon-cloud">
                                <FaCloud />
                            </div>

                            <div className="polu-floating-icon icon-ai">
                                <FaRobot />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="polu-section-heading">
                    <div>
                        <span className="polu-mini-label">
                            WHAT WE BUILD
                        </span>

                        <h3>
                            Digital Solutions
                            <span>Built to Move.</span>
                        </h3>
                    </div>

                    <p>
                        From programming and web development
                        to intelligent systems and enterprise
                        platforms, POLU-TECH delivers technology
                        designed around real-world needs.
                    </p>
                </div>

                <div
                    id="services"
                    className="polu-services-grid"
                >
                    {services.map((service) => (
                        <article
                            key={service.id}
                            className="polu-service-card"
                        >
                            <div className="polu-card-energy"></div>

                            <div className="polu-service-top">
                                <span className="polu-service-number">
                                    {service.number}
                                </span>

                                <div className="polu-service-icon">
                                    {service.icon}
                                </div>
                            </div>

                            <h4>
                                {service.title}
                            </h4>

                            <p>
                                {service.description}
                            </p>

                            <div className="polu-service-footer">
                                <span>
                                    POLU-TECH
                                </span>

                                <FaArrowRight />
                            </div>
                        </article>
                    ))}
                </div>

                <div className="polu-bottom-platform">
                    <div className="polu-platform-glow"></div>

                    <div className="polu-platform-icon">
                        <FaLayerGroup />
                    </div>

                    <div className="polu-platform-content">
                        <span>
                            ONE TECHNOLOGY PARTNER
                        </span>

                        <h3>
                            From Idea to
                            <strong> Digital Reality.</strong>
                        </h3>

                        <p>
                            We combine programming, design,
                            data, infrastructure, automation,
                            and innovation to create complete
                            digital solutions.
                        </p>
                    </div>

                    <a
                        href="#contact"
                        className="polu-platform-button"
                    >
                        Start a Project

                        <FaArrowRight />
                    </a>
                </div>

                <div className="polu-tech-footer">
                    <div>
                        <FaCogs />

                        <span>
                            ENGINEERED FOR PERFORMANCE
                        </span>
                    </div>

                    <div>
                        <FaShieldAlt />

                        <span>
                            BUILT WITH SECURITY
                        </span>
                    </div>

                    <div>
                        <FaRocket />

                        <span>
                            READY TO SCALE
                        </span>
                    </div>
                </div>
            </div>

            <style>{`
                .polu-tech-section {
                    --orange: #f97316;
                    --orange-light: #fb923c;
                    --orange-dark: #c2410c;
                    --black: #030303;
                    --black-two: #080808;
                    --gray: #8a8a8a;
                    --light-gray: #c8c8c8;
                    --white: #ffffff;
                    --mouse-x: 50%;
                    --mouse-y: 50%;

                    position: relative;
                    width: 100%;
                    min-height: 100vh;
                    overflow: hidden;
                    background:
                        radial-gradient(
                            circle at var(--mouse-x) var(--mouse-y),
                            rgba(249, 115, 22, 0.09),
                            transparent 26%
                        ),
                        linear-gradient(
                            135deg,
                            #020202 0%,
                            #080808 45%,
                            #020202 100%
                        );
                    color: var(--white);
                    font-family:
                        "Poppins",
                        "Inter",
                        Arial,
                        sans-serif;
                    isolation: isolate;
                    padding: 110px 0 55px;
                }

                .polu-tech-background {
                    position: absolute;
                    inset: 0;
                    overflow: hidden;
                    pointer-events: none;
                    z-index: -1;
                }

                .polu-grid {
                    position: absolute;
                    inset: -20%;
                    opacity: 0.22;
                    background-image:
                        linear-gradient(
                            rgba(255, 255, 255, 0.035) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(255, 255, 255, 0.035) 1px,
                            transparent 1px
                        );
                    background-size: 65px 65px;
                    transform:
                        perspective(700px)
                        rotateX(58deg)
                        translateY(20%);
                    transform-origin: center bottom;
                    animation: poluGridMove 18s linear infinite;
                }

                @keyframes poluGridMove {
                    0% {
                        transform:
                            perspective(700px)
                            rotateX(58deg)
                            translateY(10%);
                    }

                    100% {
                        transform:
                            perspective(700px)
                            rotateX(58deg)
                            translateY(17%);
                    }
                }

                .polu-energy {
                    position: absolute;
                    width: 600px;
                    height: 2px;
                    background: linear-gradient(
                        90deg,
                        transparent,
                        var(--orange),
                        #ffffff,
                        var(--orange),
                        transparent
                    );
                    opacity: 0.35;
                    filter: blur(1px);
                    box-shadow:
                        0 0 14px var(--orange),
                        0 0 30px rgba(249, 115, 22, 0.45);
                }

                .polu-energy-one {
                    top: 20%;
                    left: -700px;
                    transform: rotate(-18deg);
                    animation: energyOne 8s linear infinite;
                }

                .polu-energy-two {
                    top: 63%;
                    left: -700px;
                    transform: rotate(14deg);
                    animation: energyTwo 10s linear infinite 2s;
                }

                .polu-energy-three {
                    top: 82%;
                    left: -700px;
                    transform: rotate(-8deg);
                    animation: energyThree 12s linear infinite 1s;
                }

                @keyframes energyOne {
                    0% {
                        left: -700px;
                    }

                    100% {
                        left: 110%;
                    }
                }

                @keyframes energyTwo {
                    0% {
                        left: -800px;
                    }

                    100% {
                        left: 120%;
                    }
                }

                @keyframes energyThree {
                    0% {
                        left: -900px;
                    }

                    100% {
                        left: 125%;
                    }
                }

                .polu-orb {
                    position: absolute;
                    border-radius: 50%;
                    background: var(--orange);
                    filter: blur(100px);
                    opacity: 0.08;
                    animation: orbFloat 8s ease-in-out infinite;
                }

                .polu-orb-one {
                    width: 450px;
                    height: 450px;
                    top: -180px;
                    right: -120px;
                }

                .polu-orb-two {
                    width: 350px;
                    height: 350px;
                    bottom: 15%;
                    left: -180px;
                    animation-delay: 2s;
                }

                .polu-orb-three {
                    width: 280px;
                    height: 280px;
                    top: 45%;
                    right: 25%;
                    opacity: 0.045;
                    animation-delay: 4s;
                }

                @keyframes orbFloat {
                    0%,
                    100% {
                        transform:
                            translate3d(0, 0, 0)
                            scale(1);
                    }

                    50% {
                        transform:
                            translate3d(30px, -30px, 0)
                            scale(1.12);
                    }
                }

                .polu-particles {
                    position: absolute;
                    inset: 0;
                }

                .polu-particles span {
                    position: absolute;
                    left: var(--particle-x);
                    top: var(--particle-y);
                    width: 3px;
                    height: 3px;
                    border-radius: 50%;
                    background: var(--orange);
                    box-shadow:
                        0 0 8px var(--orange),
                        0 0 15px rgba(249, 115, 22, 0.6);
                    opacity: 0;
                    animation:
                        particleFloat
                        5s ease-in-out
                        infinite;
                    animation-delay: var(--particle-delay);
                }

                @keyframes particleFloat {
                    0% {
                        opacity: 0;
                        transform:
                            translate3d(0, 25px, 0)
                            scale(0.5);
                    }

                    30% {
                        opacity: 0.7;
                    }

                    70% {
                        opacity: 0.35;
                    }

                    100% {
                        opacity: 0;
                        transform:
                            translate3d(20px, -70px, 0)
                            scale(1.2);
                    }
                }

                .polu-tech-container {
                    position: relative;
                    width: min(1380px, 92%);
                    margin: 0 auto;
                    z-index: 2;
                }

                .polu-tech-intro {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1.05fr)
                        minmax(400px, 0.95fr);
                    gap: 80px;
                    align-items: center;
                    margin-bottom: 120px;
                }

                .polu-section-label {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    color: var(--orange);
                    font-size: 12px;
                    font-weight: 800;
                    letter-spacing: 4px;
                    margin-bottom: 22px;
                }

                .polu-label-line {
                    width: 38px;
                    height: 1px;
                    background: var(--orange);
                    box-shadow:
                        0 0 10px
                        rgba(249, 115, 22, 0.7);
                }

                .polu-main-title {
                    margin: 0;
                    max-width: 800px;
                    font-size: clamp(
                        55px,
                        7vw,
                        105px
                    );
                    line-height: 0.94;
                    font-weight: 800;
                    letter-spacing: -5px;
                }

                .polu-main-title span {
                    display: block;
                    color: transparent;
                    -webkit-text-stroke:
                        1px rgba(255, 255, 255, 0.5);
                    text-shadow:
                        0 0 30px
                        rgba(249, 115, 22, 0.05);
                }

                .polu-main-description {
                    max-width: 650px;
                    margin: 35px 0 0;
                    color: #9e9e9e;
                    font-size: 17px;
                    line-height: 1.9;
                }

                .polu-intro-actions {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 30px;
                    margin-top: 40px;
                }

                .polu-primary-button,
                .polu-platform-button {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 14px;
                    min-height: 54px;
                    padding: 0 27px;
                    border: 1px solid var(--orange);
                    border-radius: 2px;
                    background: var(--orange);
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 13px;
                    font-weight: 800;
                    letter-spacing: 0.4px;
                    transition:
                        transform 0.3s ease,
                        box-shadow 0.3s ease,
                        background 0.3s ease;
                }

                .polu-primary-button:hover,
                .polu-platform-button:hover {
                    transform: translateY(-4px);
                    background: var(--orange-light);
                    box-shadow:
                        0 12px 35px
                        rgba(249, 115, 22, 0.28);
                }

                .polu-primary-button svg,
                .polu-platform-button svg {
                    transition:
                        transform 0.3s ease;
                }

                .polu-primary-button:hover svg,
                .polu-platform-button:hover svg {
                    transform: translateX(5px);
                }

                .polu-live-status {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #777777;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 2px;
                }

                .polu-status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: var(--orange);
                    box-shadow:
                        0 0 0 5px
                        rgba(249, 115, 22, 0.08),
                        0 0 15px var(--orange);
                    animation: statusPulse 1.8s infinite;
                }

                @keyframes statusPulse {
                    0%,
                    100% {
                        box-shadow:
                            0 0 0 5px
                            rgba(249, 115, 22, 0.08),
                            0 0 15px var(--orange);
                    }

                    50% {
                        box-shadow:
                            0 0 0 11px
                            rgba(249, 115, 22, 0.02),
                            0 0 25px var(--orange);
                    }
                }

                .polu-tech-visual {
                    position: relative;
                    width: min(100%, 550px);
                    aspect-ratio: 1 / 1;
                    margin: 0 auto;
                    display: grid;
                    place-items: center;
                }

                .polu-visual-ring {
                    position: absolute;
                    border: 1px solid
                        rgba(249, 115, 22, 0.2);
                    border-radius: 50%;
                    animation:
                        visualRing
                        10s linear infinite;
                }

                .ring-one {
                    width: 92%;
                    height: 92%;
                }

                .ring-two {
                    width: 72%;
                    height: 72%;
                    border-style: dashed;
                    animation-duration: 15s;
                    animation-direction: reverse;
                }

                .ring-three {
                    width: 53%;
                    height: 53%;
                    border-color:
                        rgba(255, 255, 255, 0.1);
                    animation-duration: 20s;
                }

                @keyframes visualRing {
                    from {
                        transform: rotate(0deg);
                    }

                    to {
                        transform: rotate(360deg);
                    }
                }

                .polu-image-frame {
                    position: relative;
                    width: 62%;
                    height: 62%;
                    overflow: hidden;
                    border: 1px solid
                        rgba(249, 115, 22, 0.45);
                    background: #111111;
                    box-shadow:
                        0 0 50px
                        rgba(249, 115, 22, 0.1),
                        inset 0 0 30px
                        rgba(0, 0, 0, 0.8);
                    transform: rotate(-4deg);
                    transition:
                        transform 0.5s ease;
                }

                .polu-image-frame:hover {
                    transform:
                        rotate(0deg)
                        scale(1.03);
                }

                .polu-image-frame img {
                    width: 100%;
                    height: 100%;
                    display: block;
                    object-fit: cover;
                    filter:
                        grayscale(75%)
                        contrast(1.15);
                    transition:
                        transform 0.8s ease,
                        filter 0.8s ease;
                }

                .polu-image-frame:hover img {
                    transform: scale(1.08);
                    filter:
                        grayscale(20%)
                        contrast(1.08);
                }

                .polu-image-overlay {
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(
                            135deg,
                            rgba(249, 115, 22, 0.55),
                            transparent 50%,
                            rgba(0, 0, 0, 0.85)
                        );
                    mix-blend-mode: multiply;
                }

                .polu-image-content {
                    position: absolute;
                    left: 22px;
                    bottom: 22px;
                    display: flex;
                    flex-direction: column;
                    z-index: 2;
                }

                .polu-image-content span {
                    color: #dddddd;
                    font-size: 10px;
                    letter-spacing: 4px;
                }

                .polu-image-content strong {
                    color: #ffffff;
                    font-size: 22px;
                    letter-spacing: 3px;
                }

                .polu-floating-icon {
                    position: absolute;
                    width: 55px;
                    height: 55px;
                    display: grid;
                    place-items: center;
                    border: 1px solid
                        rgba(249, 115, 22, 0.5);
                    background:
                        rgba(12, 12, 12, 0.92);
                    color: var(--orange);
                    box-shadow:
                        0 0 25px
                        rgba(249, 115, 22, 0.14);
                    z-index: 4;
                    animation:
                        floatingIcon
                        4s ease-in-out infinite;
                }

                .polu-floating-icon svg {
                    font-size: 19px;
                }

                .icon-code {
                    top: 8%;
                    left: 5%;
                }

                .icon-database {
                    top: 19%;
                    right: 4%;
                    animation-delay: 0.8s;
                }

                .icon-cloud {
                    bottom: 13%;
                    left: 6%;
                    animation-delay: 1.5s;
                }

                .icon-ai {
                    bottom: 8%;
                    right: 9%;
                    animation-delay: 2.2s;
                }

                @keyframes floatingIcon {
                    0%,
                    100% {
                        transform:
                            translateY(0)
                            rotate(0deg);
                    }

                    50% {
                        transform:
                            translateY(-12px)
                            rotate(5deg);
                    }
                }

                .polu-section-heading {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1fr)
                        minmax(300px, 0.75fr);
                    gap: 70px;
                    align-items: end;
                    margin-bottom: 45px;
                    padding-bottom: 28px;
                    border-bottom: 1px solid
                        rgba(255, 255, 255, 0.08);
                }

                .polu-mini-label {
                    color: var(--orange);
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 3px;
                }

                .polu-section-heading h3 {
                    margin: 10px 0 0;
                    font-size: clamp(
                        34px,
                        4vw,
                        57px
                    );
                    line-height: 1.05;
                    letter-spacing: -2px;
                }

                .polu-section-heading h3 span {
                    display: block;
                    color: #777777;
                }

                .polu-section-heading p {
                    margin: 0;
                    color: #777777;
                    font-size: 14px;
                    line-height: 1.9;
                }

                .polu-services-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(4, minmax(0, 1fr));
                    gap: 15px;
                }

                .polu-service-card {
                    position: relative;
                    min-height: 330px;
                    padding: 28px;
                    overflow: hidden;
                    border: 1px solid
                        rgba(255, 255, 255, 0.08);
                    background:
                        linear-gradient(
                            145deg,
                            rgba(255, 255, 255, 0.045),
                            rgba(255, 255, 255, 0.012)
                        );
                    transition:
                        transform 0.4s ease,
                        border-color 0.4s ease,
                        background 0.4s ease,
                        box-shadow 0.4s ease;
                }

                .polu-service-card:hover {
                    transform:
                        translateY(-9px);
                    border-color:
                        rgba(249, 115, 22, 0.5);
                    background:
                        linear-gradient(
                            145deg,
                            rgba(249, 115, 22, 0.08),
                            rgba(255, 255, 255, 0.015)
                        );
                    box-shadow:
                        0 20px 50px
                        rgba(0, 0, 0, 0.35);
                }

                .polu-card-energy {
                    position: absolute;
                    width: 150px;
                    height: 150px;
                    top: -80px;
                    right: -80px;
                    border-radius: 50%;
                    background: var(--orange);
                    filter: blur(80px);
                    opacity: 0;
                    transition:
                        opacity 0.4s ease;
                }

                .polu-service-card:hover
                    .polu-card-energy {
                    opacity: 0.18;
                }

                .polu-service-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 55px;
                }

                .polu-service-number {
                    color: #505050;
                    font-family: monospace;
                    font-size: 13px;
                    font-weight: 700;
                }

                .polu-service-icon {
                    width: 52px;
                    height: 52px;
                    display: grid;
                    place-items: center;
                    border: 1px solid
                        rgba(249, 115, 22, 0.35);
                    color: var(--orange);
                    background:
                        rgba(249, 115, 22, 0.04);
                    transition:
                        background 0.3s ease,
                        transform 0.3s ease;
                }

                .polu-service-card:hover
                    .polu-service-icon {
                    background: var(--orange);
                    color: #ffffff;
                    transform:
                        rotate(-5deg)
                        scale(1.08);
                }

                .polu-service-icon svg {
                    font-size: 20px;
                }

                .polu-service-card h4 {
                    position: relative;
                    margin: 0 0 14px;
                    font-size: 18px;
                    line-height: 1.3;
                    color: #ffffff;
                }

                .polu-service-card p {
                    position: relative;
                    margin: 0;
                    color: #777777;
                    font-size: 12px;
                    line-height: 1.8;
                }

                .polu-service-footer {
                    position: absolute;
                    right: 28px;
                    bottom: 25px;
                    left: 28px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 15px;
                    border-top: 1px solid
                        rgba(255, 255, 255, 0.06);
                    color: #4e4e4e;
                    font-size: 8px;
                    font-weight: 800;
                    letter-spacing: 2px;
                }

                .polu-service-footer svg {
                    color: var(--orange);
                    font-size: 11px;
                    transition:
                        transform 0.3s ease;
                }

                .polu-service-card:hover
                    .polu-service-footer svg {
                    transform: translateX(5px);
                }

                .polu-bottom-platform {
                    position: relative;
                    display: grid;
                    grid-template-columns:
                        auto 1fr auto;
                    align-items: center;
                    gap: 30px;
                    margin-top: 70px;
                    padding: 38px 45px;
                    overflow: hidden;
                    border: 1px solid
                        rgba(249, 115, 22, 0.25);
                    background:
                        linear-gradient(
                            110deg,
                            rgba(249, 115, 22, 0.08),
                            rgba(255, 255, 255, 0.025)
                        );
                }

                .polu-platform-glow {
                    position: absolute;
                    width: 350px;
                    height: 350px;
                    right: 10%;
                    top: -250px;
                    border-radius: 50%;
                    background: var(--orange);
                    filter: blur(120px);
                    opacity: 0.08;
                    animation:
                        platformGlow
                        5s ease-in-out infinite;
                }

                @keyframes platformGlow {
                    0%,
                    100% {
                        transform: scale(1);
                    }

                    50% {
                        transform: scale(1.35);
                    }
                }

                .polu-platform-icon {
                    position: relative;
                    width: 65px;
                    height: 65px;
                    display: grid;
                    place-items: center;
                    border: 1px solid var(--orange);
                    color: var(--orange);
                    font-size: 23px;
                }

                .polu-platform-content {
                    position: relative;
                }

                .polu-platform-content > span {
                    color: var(--orange);
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 3px;
                }

                .polu-platform-content h3 {
                    margin: 5px 0 5px;
                    font-size: 25px;
                }

                .polu-platform-content h3 strong {
                    color: var(--orange);
                }

                .polu-platform-content p {
                    max-width: 720px;
                    margin: 0;
                    color: #777777;
                    font-size: 12px;
                    line-height: 1.7;
                }

                .polu-platform-button {
                    position: relative;
                    white-space: nowrap;
                }

                .polu-tech-footer {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 50px;
                    margin-top: 45px;
                    color: #4e4e4e;
                }

                .polu-tech-footer > div {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    font-size: 8px;
                    font-weight: 800;
                    letter-spacing: 2px;
                }

                .polu-tech-footer svg {
                    color: var(--orange);
                    font-size: 13px;
                }

                @media (max-width: 1200px) {
                    .polu-tech-intro {
                        gap: 40px;
                    }

                    .polu-services-grid {
                        grid-template-columns:
                            repeat(2, minmax(0, 1fr));
                    }
                }

                @media (max-width: 900px) {
                    .polu-tech-section {
                        padding-top: 80px;
                    }

                    .polu-tech-intro {
                        grid-template-columns: 1fr;
                        margin-bottom: 90px;
                    }

                    .polu-main-title {
                        font-size: clamp(
                            55px,
                            12vw,
                            85px
                        );
                    }

                    .polu-tech-visual {
                        max-width: 500px;
                        margin-top: 10px;
                    }

                    .polu-section-heading {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }

                    .polu-bottom-platform {
                        grid-template-columns:
                            auto 1fr;
                    }

                    .polu-platform-button {
                        grid-column: 1 / -1;
                        width: max-content;
                    }
                }

                @media (max-width: 650px) {
                    .polu-tech-section {
                        padding:
                            70px 0
                            35px;
                    }

                    .polu-tech-container {
                        width: 90%;
                    }

                    .polu-main-title {
                        font-size: 52px;
                        letter-spacing: -3px;
                    }

                    .polu-main-description {
                        font-size: 14px;
                    }

                    .polu-intro-actions {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .polu-services-grid {
                        grid-template-columns: 1fr;
                    }

                    .polu-service-card {
                        min-height: 310px;
                    }

                    .polu-bottom-platform {
                        grid-template-columns: 1fr;
                        padding: 30px;
                    }

                    .polu-platform-button {
                        width: 100%;
                    }

                    .polu-tech-footer {
                        align-items: flex-start;
                        flex-direction: column;
                        gap: 18px;
                    }

                    .polu-floating-icon {
                        width: 45px;
                        height: 45px;
                    }

                    .polu-floating-icon svg {
                        font-size: 15px;
                    }

                    .polu-image-frame {
                        width: 65%;
                        height: 65%;
                    }
                }

                @media (max-width: 420px) {
                    .polu-main-title {
                        font-size: 45px;
                    }

                    .polu-section-heading h3 {
                        font-size: 32px;
                    }

                    .polu-service-card {
                        padding: 23px;
                    }

                    .polu-service-top {
                        margin-bottom: 40px;
                    }

                    .polu-image-content strong {
                        font-size: 17px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .polu-tech-section *,
                    .polu-tech-section *::before,
                    .polu-tech-section *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        scroll-behavior: auto !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default Card;